// Course Workspace — dreigeteilter Screen nach dem abgenommenen Designkonzept
// (analysis/design-benchmark/06_…md, 2026-08-13): Ziel → Methode → Prüfung.
// Slice 2: Fokus-Modus (Klick = verknüpfte Karten glowen, Rest dimmt, ESC
// hebt auf), Matrix-Zellen-Klick → Paar-Highlight, KAI-Drawer (kontextuell,
// §5a) mit echtem Chat-Backend.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";

import {
  api,
  APIError,
  isFeasibilityDetail,
  isQuotaError,
  type AnalysisReport,
  type ContentLang,
  type Course,
  type DetectedFlaw,
  type WizardCatalog,
} from "@/api/client";
import LoadingOverlay from "@/components/Loading";
import QuotaExhaustedCard from "@/components/QuotaExhaustedCard";
import AlignmentMatrixSection from "@/components/Workspace/AlignmentMatrixSection";
import EmptyState from "@/components/Workspace/EmptyState";
import {
  AtCard,
  IloCard,
  TlaCard,
  type FocusState,
} from "@/components/Workspace/EntityCards";
import KaiDrawer from "@/components/Workspace/KaiDrawer";
import WorkspaceHeader from "@/components/Workspace/WorkspaceHeader";

type Focus =
  | { kind: "entity"; id: string }
  | { kind: "pair"; ids: [string, string] }
  | null;

function short(text: string | undefined, n = 42): string {
  const s = (text ?? "").trim();
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

export default function CourseWorkspace() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [course, setCourse] = useState<Course | null>(null);
  const [latestReport, setLatestReport] = useState<AnalysisReport | null>(null);
  const [catalog, setCatalog] = useState<WizardCatalog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [quota, setQuota] = useState<unknown>(null);
  const [auditing, setAuditing] = useState(false);
  const [focus, setFocus] = useState<Focus>(null);
  const [kai, setKai] = useState<{
    open: boolean;
    contextLabel: string;
    entitySummary?: string;
    /** Ziel für „An Methode anhängen" (nur TLA-Karten, v1). */
    attachTlaId?: string;
  }>({
    open: false,
    contextLabel: "",
  });

  const load = useCallback(async () => {
    if (!id) return;
    const [c, reports, cat] = await Promise.all([
      api.getCourse(id),
      api.listReports(id),
      api.wizardCatalog(),
    ]);
    setCourse(c);
    // Backend liefert desc nach generated_at; sicherheitshalber sortieren.
    const sorted = [...reports].sort((a, b) =>
      b.generated_at.localeCompare(a.generated_at),
    );
    setLatestReport(sorted[0] ?? null);
    setCatalog(cat);
  }, [id]);

  useEffect(() => {
    load().catch((e) =>
      setError(e instanceof APIError ? e.detail : String(e)),
    );
  }, [load]);

  const runAudit = async () => {
    if (!id || auditing) return;
    setAuditing(true);
    setAuditError(null);
    try {
      const report = await api.runAudit(id);
      await load();
      // Direkt in den frischen Bericht springen — sonst wirkt der Button
      // nach ~2 Min Laufzeit „tot" (Live-Fund Franz, 2026-08-14).
      navigate(`/courses/${id}/audit/${report.id}`);
    } catch (e) {
      if (isQuotaError(e)) {
        setQuota(e.data);
      } else if (e instanceof APIError && isFeasibilityDetail(e.data)) {
        setAuditError(e.detail);
      } else {
        setAuditError(e instanceof APIError ? e.detail : String(e));
      }
    } finally {
      setAuditing(false);
    }
  };

  // Befunde des letzten Reports nach Ziel-Entität gruppieren (Marker direkt
  // an der Karte, Konzept §2).
  const flawsByTarget = useMemo(() => {
    const map = {
      ilo: new Map<string, DetectedFlaw[]>(),
      tla: new Map<string, DetectedFlaw[]>(),
      at: new Map<string, DetectedFlaw[]>(),
    };
    for (const f of latestReport?.detected_flaws ?? []) {
      const push = (m: Map<string, DetectedFlaw[]>, k?: string | null) => {
        if (!k) return;
        m.set(k, [...(m.get(k) ?? []), f]);
      };
      push(map.ilo, f.target_ilo_id);
      push(map.tla, f.target_tla_id);
      push(map.at, f.target_at_id);
    }
    return map;
  }, [latestReport]);

  // ILO-Nummerierung (Position in der Liste = Nummer im Kurs).
  const iloNumbers = useMemo(() => {
    const m = new Map<string, number>();
    (course?.ilos ?? []).forEach((ilo, i) => m.set(ilo.id, i + 1));
    return m;
  }, [course]);

  // Verknüpfungs-Karte für den Fokus-Modus: entityId → verknüpfte entityIds.
  // ILO↔TLA und ILO↔AT kommen aus den linked_ilo_ids der TLA/AT-Seite;
  // ILOs bekommen ihre Links per Umkehrung.
  const linksById = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const tla of course?.tlas ?? []) {
      m.set(tla.id, [...tla.linked_ilo_ids]);
      for (const iloId of tla.linked_ilo_ids) {
        m.set(iloId, [...(m.get(iloId) ?? []), tla.id]);
      }
    }
    for (const at of course?.ats ?? []) {
      m.set(at.id, [...(m.get(at.id) ?? []), ...at.linked_ilo_ids]);
      for (const iloId of at.linked_ilo_ids) {
        m.set(iloId, [...(m.get(iloId) ?? []), at.id]);
      }
    }
    return m;
  }, [course]);

  const focusStateFor = useCallback(
    (entityId: string): FocusState => {
      if (!focus) return "none";
      if (focus.kind === "entity") {
        if (focus.id === entityId) return "origin";
        return (linksById.get(focus.id) ?? []).includes(entityId)
          ? "hit"
          : "dim";
      }
      return focus.ids.includes(entityId) ? "hit" : "dim";
    },
    [focus, linksById],
  );

  const selectEntity = (entityId: string) => {
    setFocus((f) =>
      f?.kind === "entity" && f.id === entityId
        ? null
        : { kind: "entity", id: entityId },
    );
  };

  // Matrix-Zellen-Klick: Paar in den Spalten highlighten + hinscrollen.
  const focusPair = (iloId: string, atId: string) => {
    setFocus({ kind: "pair", ids: [iloId, atId] });
    setTimeout(() => {
      for (const eid of [iloId, atId]) {
        document
          .getElementById(`wscard-${eid}`)
          ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 50);
  };

  // ESC hebt den Fokus auf (wie im abgenommenen Mockup).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFocus(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const openKai = (contextLabel: string, entitySummary?: string, attachTlaId?: string) =>
    setKai({ open: true, contextLabel, entitySummary, attachTlaId });

  // KAI-Antwort als Anhang an der Methoden-Karte speichern (extras.attachments —
  // Merge im Frontend, PATCH ersetzt das Dict als Ganzes).
  const attachToTla = async (tlaId: string, content: string) => {
    const tla = tlas.find((t) => t.id === tlaId);
    if (!tla) return;
    const extras = tla.extras ?? {};
    const attachments = [...(extras.attachments ?? [])];
    const firstLine = content.split("\n").find((l) => l.trim()) ?? "";
    const title = firstLine.replace(/^[#\s*]+/, "").slice(0, 80) || t("workspace.attachment");
    attachments.push({ title, content, created_at: new Date().toISOString() });
    await api.updateTLA(id!, tlaId, { extras: { ...extras, attachments } });
    await load();
  };

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center text-ws-text-2">
        {t("common.error")}: {error}
      </div>
    );
  }
  if (!course) return <LoadingOverlay message={t("common.loadingEllipsis")} />;

  const ilos = course.ilos ?? [];
  const tlas = course.tlas ?? [];
  const ats = course.ats ?? [];
  const contentLang: ContentLang =
    (course.metadata?.content_lang as ContentLang | undefined) ?? "de";

  const linkedTlaCount = (iloId: string) =>
    tlas.filter((tla) => tla.linked_ilo_ids.includes(iloId)).length;
  const linkedAtCount = (iloId: string) =>
    ats.filter((at) => at.linked_ilo_ids.includes(iloId)).length;
  const iloNumsFor = (linkedIds: string[]) =>
    linkedIds
      .map((lid) => iloNumbers.get(lid))
      .filter((n): n is number => n != null)
      .sort((a, b) => a - b);

  return (
    <div className="min-h-screen text-[15px] leading-[1.55] text-ws-text">
      <WorkspaceHeader
        course={course}
        latestReport={latestReport}
        catalog={catalog}
        auditing={auditing}
        onRunAudit={runAudit}
      />
      {quota !== null && (
        <div className="mx-6 mt-3">
          <QuotaExhaustedCard detail={quota} onDismiss={() => setQuota(null)} />
        </div>
      )}
      {auditError && (
        <p className="mx-6 mt-3 rounded-lg border border-[rgba(245,166,35,.4)] bg-[rgba(245,166,35,.07)] px-4 py-2.5 text-[13px] text-ws-amber">
          {auditError}
        </p>
      )}

      <main className="grid items-start gap-4 px-6 py-5 md:grid-cols-[28fr_36fr_36fr]">
        {/* LINKS: Lernziele */}
        <section>
          <h2 className="mb-1 text-[13px] font-bold uppercase tracking-[0.06em] text-ws-text-2">
            {t("workspace.col.iloTitle")}
            <Link
              to={`/courses/${id}/edit?step=2`}
              className="ml-2 align-middle text-[11px] font-semibold normal-case tracking-normal text-ws-text-3 transition hover:text-ws-glow"
              title={t("workspace.col.iloTitle")}
            >
              ✎ {t("workspace.editColumn")}
            </Link>
          </h2>
          <p className="mb-3.5 text-[12.5px] text-ws-text-3">
            {t("workspace.col.iloSub")}
          </p>
          {ilos.length === 0 ? (
            <EmptyState
              quipKey="workspace.empty.iloQuip"
              bodyKey="workspace.empty.iloBody"
              cta={{
                labelKey: "workspace.empty.iloCta",
                onClick: () =>
                  openKai(`${t("workspace.kai.newIlo")} · ${course.title}`),
              }}
            />
          ) : (
            ilos.map((ilo, i) => (
              <IloCard
                key={ilo.id}
                ilo={ilo}
                linkedTlaCount={linkedTlaCount(ilo.id)}
                linkedAtCount={linkedAtCount(ilo.id)}
                flaws={flawsByTarget.ilo.get(ilo.id) ?? []}
                focus={focusStateFor(ilo.id)}
                onSelect={() => selectEntity(ilo.id)}
                onSaveText={async (text) => {
                  await api.updateILO(id!, ilo.id, { text });
                  await load();
                }}
                onKai={() =>
                  openKai(
                    `${t("workspace.kai.ilo")} ${i + 1} · ${short(ilo.text)}`,
                    `Typ: Lernziel ${i + 1}\nText: „${ilo.text}“` +
                      (ilo.action_verb ? `\nAktionsverb: ${ilo.action_verb}` : "") +
                      (ilo.bloom_level ? `\nBloom: K${ilo.bloom_level}` : "") +
                      (ilo.bender_depth ? `\nKompetenzstufe: ${ilo.bender_depth}` : "") +
                      (ilo.content_area ? `\nInhaltsbereich: ${ilo.content_area}` : ""),
                  )
                }
              />
            ))
          )}
        </section>

        {/* MITTE: Methoden */}
        <section>
          <h2 className="mb-1 text-[13px] font-bold uppercase tracking-[0.06em] text-ws-text-2">
            {t("workspace.col.tlaTitle")}
            <Link
              to={`/courses/${id}/edit?step=3`}
              className="ml-2 align-middle text-[11px] font-semibold normal-case tracking-normal text-ws-text-3 transition hover:text-ws-glow"
              title={t("workspace.col.tlaTitle")}
            >
              ✎ {t("workspace.editColumn")}
            </Link>
          </h2>
          <p className="mb-3.5 text-[12.5px] text-ws-text-3">
            {t("workspace.col.tlaSub")}
          </p>
          {tlas.length === 0 ? (
            <EmptyState
              quipKey="workspace.empty.tlaQuip"
              bodyKey="workspace.empty.tlaBody"
              locked={ilos.length === 0}
            />
          ) : (
            tlas.map((tla, i) => (
              <TlaCard
                key={tla.id}
                tla={tla}
                linkedIloNumbers={iloNumsFor(tla.linked_ilo_ids)}
                flaws={flawsByTarget.tla.get(tla.id) ?? []}
                catalog={catalog}
                focus={focusStateFor(tla.id)}
                onSelect={() => selectEntity(tla.id)}
                onSaveText={async (text) => {
                  await api.updateTLA(id!, tla.id, { description: text });
                  await load();
                }}
                onKai={() =>
                  openKai(
                    `${t("workspace.kai.tla")} ${i + 1} · ${short(tla.description ?? tla.type)}`,
                    `Typ: Methode${tla.type ? ` (${tla.type})` : ""}\nText: „${tla.description ?? tla.type}“` +
                      (tla.duration_minutes ? `\nDauer: ${tla.duration_minutes} min` : "") +
                      (tla.modality ? `\nModalität: ${tla.modality}` : "") +
                      `\nBereitet vor auf: ${iloNumsFor(tla.linked_ilo_ids).map((n) => `ILO ${n}`).join(", ") || "—"}`,
                    tla.id,
                  )
                }
              />
            ))
          )}
        </section>

        {/* RECHTS: Prüfung */}
        <section>
          <h2 className="mb-1 text-[13px] font-bold uppercase tracking-[0.06em] text-ws-text-2">
            {t("workspace.col.atTitle")}
            <Link
              to={`/courses/${id}/edit?step=4`}
              className="ml-2 align-middle text-[11px] font-semibold normal-case tracking-normal text-ws-text-3 transition hover:text-ws-glow"
              title={t("workspace.col.atTitle")}
            >
              ✎ {t("workspace.editColumn")}
            </Link>
          </h2>
          <p className="mb-3.5 text-[12.5px] text-ws-text-3">
            {t("workspace.col.atSub")}
          </p>
          {ats.length === 0 ? (
            <EmptyState
              quipKey="workspace.empty.atQuip"
              bodyKey="workspace.empty.atBody"
              locked={ilos.length === 0 || tlas.length === 0}
            />
          ) : (
            ats.map((at, i) => (
              <AtCard
                key={at.id}
                at={at}
                linkedIloNumbers={iloNumsFor(at.linked_ilo_ids)}
                flaws={flawsByTarget.at.get(at.id) ?? []}
                catalog={catalog}
                focus={focusStateFor(at.id)}
                onSelect={() => selectEntity(at.id)}
                onSaveText={async (text) => {
                  await api.updateAT(id!, at.id, { description: text });
                  await load();
                }}
                onKai={() =>
                  openKai(
                    `${t("workspace.kai.at")} ${i + 1} · ${short(at.description ?? at.type)}`,
                    `Typ: Prüfung${at.type ? ` (${at.type})` : ""}\nText: „${at.description ?? at.type}“` +
                      (at.format ? `\nFormat: ${at.format}` : "") +
                      `\nPrüft: ${iloNumsFor(at.linked_ilo_ids).map((n) => `ILO ${n}`).join(", ") || "—"}`,
                  )
                }
              />
            ))
          )}
        </section>
      </main>

      <AlignmentMatrixSection
        report={latestReport}
        ilos={ilos}
        ats={ats}
        onPairFocus={focusPair}
      />

      <KaiDrawer
        open={kai.open}
        contextLabel={kai.contextLabel}
        entitySummary={kai.entitySummary}
        onAttach={
          kai.attachTlaId
            ? (content) => attachToTla(kai.attachTlaId!, content)
            : undefined
        }
        attachLabel={t("workspace.attachToMethod")}
        attachedLabel={t("workspace.attachedToMethod")}
        courseId={course.id}
        language={contentLang}
        onClose={() => setKai((k) => ({ ...k, open: false }))}
        onApplied={load}
      />
      {/* Zusammengeschobener Zustand (Franz 2026-08-15: die offene Schublade
          ist „erschlagend"): nach dem ersten Öffnen bleibt nur eine schmale
          KAI-Leiste am rechten Rand, ein Klick klappt sie wieder auf. */}
      {!kai.open && kai.contextLabel && (
        <button
          onClick={() => setKai((k) => ({ ...k, open: true }))}
          title={t("workspace.kai.reopen")}
          className="fixed right-0 top-1/2 z-40 -translate-y-1/2 rounded-l-lg border border-r-0 border-ws-surface-border bg-ws-card px-2 py-4 text-sm font-semibold text-ws-surface-muted shadow-lg transition hover:text-ca-surface-accent"
          style={{ writingMode: "vertical-rl" }}
        >
          ✦ KAI
        </button>
      )}
    </div>
  );
}
